import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import addSquareIcon from '@assets/img/icons/add-square.svg';
import refreshIcon from '@assets/img/icons/refresh.svg';
import subtractSquareIcon from '@assets/img/icons/subtract-square.svg';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { TextInput as OldTextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IconButton } from '@components/IconButton';
import { QueryContainer } from '@components/QueryContainer';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { routePlanStrings } from '@data/routePlan';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { useInfiniteVesselSchedules } from '@hooks/queries/dashboard/useInifiniteVesselSchedules';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import {
  VesselScheduleItem,
  VesselScheduleQueryWithoutPage,
  vesselScheduleQueryWithoutPageInitialValues,
} from '@models/VesselSchedule';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import useIntersection from 'react-use/lib/useIntersection';

const strings = createStrings({
  ja: {
    selectVesselScheduleItemTitle: '本船スケジュールから選択してください',
    addVesselSchedule: '本船情報を追加',
    editOrDeleteVesselSchedule: '本船情報を編集または削除',
    vesselWithoutAtdMessage: '* ATDが指定されてないスケジュールだけを指定できます。',
    noMoreVessels: 'お探しの本船スケジュールは以上となります',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialog: {
    maxWidth: '960px !important',
  },
  formContainer: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  input: {
    width: 0,
  },
  joinedInput: {
    width: 120,
  },
  label: {
    minWidth: 'initial',
    width: 50,
    textAlign: 'right',
  },
  joinLabel: {
    ...common.middleText,
    ...common.textGray700,
    paddingTop: 5,
    paddingLeft: 12,
    paddingRight: 12,
  },
  vesselScheduleListContainer: {
    ...common.middleText,
    paddingLeft: 24,
    paddingRight: 24,
    height: 300,
    overflow: 'auto',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  vesselScheduleListTable: {
    width: '100%',
    '& th': {
      ...common.fontWeight500,
      lineHeight: '50px',
      textAlign: 'left',
      position: 'sticky',
      top: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: 10,
    },
    '& td': {
      ...common.textGray700,
      lineHeight: '40px',
      maxWidth: 200,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  actionsContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    columnGap: '1.5rem',
    paddingTop: '1rem',
    paddingBottom: '1rem',
    paddingLeft: 24,
    paddingRight: 24,
    [theme.breakpoints.down('sm')]: {
      columnGap: '8rem',
      rowGap: '0.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
    },
  },
  dialogContent: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 0,
  },
  checkboxRoot: {
    margin: -5,
  },
  pickupInfoContainer: {
    ...common.middleText,
    ...common.flexInline,
    ...common.textGray700,
    columnGap: 30,
    padding: '12px 0px',
    marginBottom: 12,
    marginLeft: 24,
    marginRight: 24,
    borderBottom: `1px dashed ${theme.palette.grey[200]}`,
  },
  pickupInfoKeyValueContainer: {
    ...common.flexInline,
    columnGap: 16,
  },
}));

const TextInput: typeof OldTextInput = (props) => {
  const classes = useStyles();
  const { classes: customClasses, ...rest } = props;

  return (
    <OldTextInput
      classes={{ input: classes.input, ...customClasses }}
      formClasses={{ label: classes.label }}
      {...rest}
    />
  );
};

interface VesselScheduleListProps {
  items: VesselScheduleItem[];
  selectedId: number | null;
  onChangeSelectedId: (id: number | null) => void;
  isEnd?: boolean;
  onScrollEnd?: () => void;
}

const VesselScheduleList: React.FC<VesselScheduleListProps> = (props) => {
  const { items, selectedId, onChangeSelectedId, isEnd, onScrollEnd } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const containerRef = useRef<HTMLDivElement | null>(null);

  const tailRef = useRef<HTMLDivElement | null>(null);

  const intersection = useIntersection(tailRef, {
    root: containerRef.current,
  });

  useEffect(() => {
    intersection?.isIntersecting && onScrollEnd?.();
  }, [intersection?.isIntersecting]);

  return (
    <div ref={containerRef} className={classes.vesselScheduleListContainer}>
      <table className={classes.vesselScheduleListTable}>
        <thead>
          <tr>
            <th></th>
            <th>{vesselScheduleStrings.no}</th>
            <th>{vesselScheduleStrings.vessel}</th>
            <th>{vesselScheduleStrings.voyage}</th>
            <th>{vesselScheduleStrings.pol}</th>
            <th>{vesselScheduleStrings.pod}</th>
            <th>{vesselScheduleStrings.etd}</th>
            <th>{vesselScheduleStrings.eta}</th>
          </tr>
        </thead>

        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <Checkbox
                  classes={{
                    root: classes.checkboxRoot,
                  }}
                  color="primary"
                  checked={selectedId === item.id}
                  onClick={() => {
                    if (selectedId !== item.id) {
                      onChangeSelectedId(item.id);
                    } else {
                      onChangeSelectedId(null);
                    }
                  }}
                />
              </td>
              <td>{item.id}</td>
              <td>{item.vessel ?? '-'}</td>
              <td>{item.voyage ?? '-'}</td>
              <td>{item.pol ?? '-'}</td>
              <td>{item.pod ?? '-'}</td>
              <td>{item.etd ?? '-'}</td>
              <td>{item.eta ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {isEnd && (
        <div
          className={clsx(classes.textAlignCenter, classes.textGray400, classes.smallerText)}
          style={{ marginBottom: '1rem' }}
        >
          {strings.noMoreVessels}
        </div>
      )}

      <div ref={tailRef} style={{ height: 1 }} />
    </div>
  );
};

export interface VesselScheduleFilterProps {
  open: boolean;
  onClose: () => void;
  selectedId: number | null;
  onChangeSelectedItem: (id: VesselScheduleItem) => void;
}

export const VesselScheduleFilter: React.FC<VesselScheduleFilterProps> = (props) => {
  const { open, onClose, selectedId, onChangeSelectedItem } = props;

  const { bookingInquiry, preAlert } = useContext(BookingDetailsContext);

  const hasPickups = !!bookingInquiry.cargos.cargos_by_pickup_address;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [currentSelectedId, setCurrentSelectedId] = useState(selectedId);

  const minimumEtd = preAlert.minimum_etd;

  const initialValues = useMemo(
    () => ({
      ...vesselScheduleQueryWithoutPageInitialValues,
      etd_after: minimumEtd,
      eta_after: minimumEtd,
    }),
    [minimumEtd],
  );

  const [query, setQuery] = useState<VesselScheduleQueryWithoutPage>(initialValues);

  const vesselSchedulesQuery = useInfiniteVesselSchedules(
    {
      vessel: query.vessel || undefined,
      voyage: query.voyage || undefined,
      pol: bookingInquiry.route_plan?.pol,
      pod: bookingInquiry.route_plan?.pod,
      etd_after: query.etd_after || undefined,
      eta_after: query.eta_after || undefined,
      etd_before: query.etd_before || undefined,
      eta_before: query.eta_before || undefined,
      ordering: 'etd',
      has_atd: false,
    },
    open,
  );

  const items = useMemo(
    () => vesselSchedulesQuery.data?.pages.map((p) => p.results).flat() ?? [],
    [vesselSchedulesQuery.data],
  );

  const renderPickupInfoKeyValue = (key: React.ReactNode, value: React.ReactNode) => {
    return (
      <div className={classes.pickupInfoKeyValueContainer}>
        <div>{key}</div>
        <div>{value}</div>
      </div>
    );
  };

  const renderPickupInfo = () => {
    return (
      <div className={classes.pickupInfoContainer}>
        {renderPickupInfoKeyValue(
          'Pickup Option',
          hasPickups ? bookingInquiryStrings.needsPickup : bookingInquiryStrings.noPickup,
        )}

        {hasPickups && (
          <>
            {renderPickupInfoKeyValue(
              'Pickup Date',
              dayjs(bookingInquiry.pickup_date).format('YYYY-MM-DD'),
            )}

            {renderPickupInfoKeyValue(
              'Pickup Leadtime',
              bookingInquiry.route_plan_simulator_result?.simulator_pickup_days ?? '-',
            )}
          </>
        )}

        {!hasPickups &&
          renderPickupInfoKeyValue(
            'CFS Cut-off Date',
            dayjs(bookingInquiry.move_in_date).format('YYYY-MM-DD'),
          )}

        {renderPickupInfoKeyValue(
          routePlanStrings.receivingWarehouseDays,
          bookingInquiry.route_plan_simulator_result?.receiving_warehouse_days,
        )}

        {renderPickupInfoKeyValue(
          routePlanStrings.exportWarehouseDays,
          bookingInquiry.route_plan_simulator_result?.export_warehouse_days,
        )}
      </div>
    );
  };

  const renderSearchForm = () => (
    <Formik<VesselScheduleQueryWithoutPage>
      initialValues={query}
      enableReinitialize
      onSubmit={(values) => setQuery(values)}
    >
      <Form className={classes.formContainer}>
        <Grid container spacing={0}>
          <Grid item xs={3}>
            <TextInput name="vessel" label={vesselScheduleStrings.vessel} />
          </Grid>
          <Grid item xs={7} className={classes.flexInline}>
            <TextInput
              name="etd_after"
              label={vesselScheduleStrings.etd}
              classes={{ input: classes.joinedInput }}
              type="date"
              min={minimumEtd ?? undefined}
            />
            <div className={classes.joinLabel}>{formStrings.from}</div>
            <TextInput
              name="etd_before"
              noLabel
              classes={{ input: classes.joinedInput }}
              type="date"
              min={minimumEtd ?? undefined}
            />
            <div className={classes.joinLabel}>{formStrings.until}</div>
          </Grid>
          <Grid item xs={1}>
            <Button
              color="primary"
              text={formStrings.reset}
              size="small"
              style={{ padding: 0, width: '100%' }}
              type="button"
              onClick={() => setQuery(initialValues)}
            />
          </Grid>

          <Grid item xs={12}>
            <div style={{ height: 12 }} />
          </Grid>

          <Grid item xs={3}>
            <TextInput name="voyage" label={vesselScheduleStrings.voyage} />
          </Grid>
          <Grid item xs={7} className={classes.flexInline}>
            <TextInput
              name="eta_after"
              label={vesselScheduleStrings.eta}
              classes={{ input: classes.joinedInput }}
              type="date"
              min={minimumEtd ?? undefined}
            />
            <div className={classes.joinLabel}>{formStrings.from}</div>
            <TextInput
              name="eta_before"
              noLabel
              classes={{ input: classes.joinedInput }}
              type="date"
              min={minimumEtd ?? undefined}
            />
            <div className={classes.joinLabel}>{formStrings.until}</div>
          </Grid>
          <Grid item xs={1}>
            <Button
              color="primary"
              text={formStrings.search + formStrings.suru}
              size="small"
              style={{ padding: 0, width: '100%' }}
            />
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );

  const renderActions = () => (
    <div className={classes.actionsContainer}>
      <IconButton
        icon={<img src={addSquareIcon} alt="Add Icon" />}
        text={strings.addVesselSchedule}
        onClick={() => window.open(`${url('/admin/widgets/VesselSchedule')}?action=create`)}
      />
      <IconButton
        icon={<img src={subtractSquareIcon} alt="Minus Icon" />}
        text={strings.editOrDeleteVesselSchedule}
        onClick={() => window.open(url('/admin/widgets/VesselSchedule'))}
      />
      <IconButton
        icon={<img src={refreshIcon} alt="Refresh Icon" />}
        text={formStrings.update}
        disabled={vesselSchedulesQuery.isFetching}
        onClick={() => {
          vesselSchedulesQuery.remove();
          vesselSchedulesQuery.refetch();
        }}
      />
    </div>
  );

  return (
    <ActionableDialog
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.dialog,
      }}
      muiDialogProps={{
        disablePortal: false,
      }}
    >
      <ActionableDialogTitle>{strings.selectVesselScheduleItemTitle}</ActionableDialogTitle>
      <ActionableDialogContent classes={{ root: classes.dialogContent }}>
        {renderPickupInfo()}
        {renderSearchForm()}
        {renderActions()}
        <QueryContainer style={{ height: 300 }} queries={[vesselSchedulesQuery]}>
          <VesselScheduleList
            items={items}
            selectedId={currentSelectedId}
            onChangeSelectedId={setCurrentSelectedId}
            onScrollEnd={() => vesselSchedulesQuery.fetchNextPage()}
            isEnd={items.length >= (vesselSchedulesQuery.data?.pages[0]?.count ?? Infinity)}
          />
        </QueryContainer>
      </ActionableDialogContent>

      <ActionableDialogContentDivider />

      <div className={clsx(classes.flexInline, classes.itemsCenter)} style={{ padding: '1rem' }}>
        <span className={clsx(classes.exSmallerText, classes.textPrimary, classes.flex1)}>
          {strings.vesselWithoutAtdMessage}
        </span>

        <Button
          color="primary"
          text={formStrings.ok}
          size="small"
          disabled={currentSelectedId == null}
          onClick={() => {
            const item = items.find((item) => item.id === currentSelectedId)!;
            onChangeSelectedItem(item);
            onClose();
          }}
        />
      </div>
    </ActionableDialog>
  );
};
